import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import { BRIEFING_LANGUAGES, SCHOOL_DEPARTMENTS, SCHOOL_PERMISSIONS, type BriefingLanguage } from '@jupi/shared'
import { RemoteContent, Title, useAuth } from '@jupi/ui'
import { useBriefingPacks } from '../../api/briefings'

// The briefing packs this member may read, by course, with an "open to all"
// group; instructors can add and edit.
export default function BriefingsPage() {
  const { t, i18n } = useTranslation()
  const { can } = useAuth()
  const { data: packs = [], isLoading, error } = useBriefingPacks()
  const ui = i18n.language.slice(0, 2)
  const [language, setLanguage] = useState<BriefingLanguage | 'all'>(
    (BRIEFING_LANGUAGES as readonly string[]).includes(ui) ? (ui as BriefingLanguage) : 'all',
  )
  const canWrite = can(...SCHOOL_DEPARTMENTS.flatMap((d) => [SCHOOL_PERMISSIONS[d].instruct, SCHOOL_PERMISSIONS[d].manage]))
  const shown = packs.filter((p) => language === 'all' || p.language === language)
  // The API orders by course, open packs last.
  const groups = [...new Set(shown.map((p) => p.courseCode ?? ''))]

  return (
    <Box>
      <Title label={t('briefings.title')}>
        {canWrite ? (
          <Button variant="contained" component={RouterLink} to="/school/briefings/new" startIcon={<Icon icon="mdi:plus" />}>
            {t('briefings.new')}
          </Button>
        ) : null}
      </Title>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={language}
        onChange={(_, v) => v && setLanguage(v)}
        aria-label={t('briefings.language')}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="all">{t('briefings.allLanguages')}</ToggleButton>
        {BRIEFING_LANGUAGES.map((l) => (
          <ToggleButton key={l} value={l}>
            {t(`briefings.lang_${l}`)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <RemoteContent isLoading={isLoading} error={error}>
        {shown.length === 0 && !isLoading ? <Typography color="text.secondary">{t('briefings.empty')}</Typography> : null}
        {groups.map((group) => {
          const own = shown.filter((p) => (p.courseCode ?? '') === group)
          return (
            <Box key={group} sx={{ mb: 3 }}>
              <Typography variant="overline" color="text.secondary">
                {group === '' ? t('briefings.openToAll') : own[0]!.courseName}
              </Typography>
              <Paper variant="outlined">
                <List disablePadding>
                  {own.map((p) => (
                    <ListItemButton key={p.packId} component={RouterLink} to={`/school/briefings/${p.packId}`} divider>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Icon icon="mdi:book-open-page-variant-outline" width={24} />
                      </ListItemIcon>
                      <ListItemText primary={p.title} secondary={p.description} />
                      <Chip size="small" label={t(`briefings.lang_${p.language}`)} sx={{ mr: 1 }} />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            </Box>
          )
        })}
      </RemoteContent>
    </Box>
  )
}
